import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMissingFirebaseKeys } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import './Login.css';

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M12 10.2v3.6h5.1c-.2 1.2-1.6 3.5-5.1 3.5-3.1 0-5.6-2.5-5.6-5.6s2.5-5.6 5.6-5.6c1.8 0 3 .8 3.7 1.4l2.5-2.4C16.9 3.6 14.7 2.6 12 2.6 6.9 2.6 2.7 6.8 2.7 12s4.2 9.4 9.3 9.4c5.4 0 9-3.8 9-9.2 0-.6-.1-1.1-.2-1.6H12z"
      />
      <path
        fill="#34A853"
        d="M3.9 7.5l3 2.2c.8-2.4 2.9-4 5.4-4 1.8 0 3 .8 3.7 1.4l2.5-2.4C16.9 3.6 14.7 2.6 12 2.6 9 2.6 6.4 4.1 5 6.5l-1.1-1z"
      />
      <path
        fill="#4A90E2"
        d="M12 21.4c2.7 0 5-1 6.6-2.7l-3.1-2.5c-.8.6-1.9 1-3.5 1-2.7 0-4.9-1.8-5.7-4.3l-3.1 2.4c1.5 3 4.6 5.1 8.3 5.1z"
      />
      <path
        fill="#FBBC05"
        d="M21.3 12.7c0-.5 0-1-.1-1.5H12v3.1h5.2c-.2 1.2-1 2.9-2.8 4l3.1 2.4c1.8-1.7 2.8-4.2 2.8-7z"
      />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M12 3l7 3v6c0 4.5-3 7.8-7 9-4-1.2-7-4.5-7-9V6l7-3z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

function BuildingIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M9 7h2M13 7h2M9 11h2M13 11h2M9 15h2M13 15h2M10 21v-3h4v3" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 12.5l2.3 2.3 4.7-4.8" />
    </svg>
  );
}

function SignalIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="2" />
      <path d="M7.8 7.8a6 6 0 000 8.4M16.2 7.8a6 6 0 010 8.4M5 5a10 10 0 000 14M19 5a10 10 0 010 14" />
    </svg>
  );
}

const MODES = {
  internal: {
    label: 'Internal SIA Team',
    icon: ShieldIcon,
    title: 'Operations Console',
    text: 'For auditors, team leads and admins. Sign in with the Google account your administrator added to the system.',
    button: 'Sign in & enter Operations Console',
  },
  client: {
    label: 'Customer / SPOC',
    icon: BuildingIcon,
    title: 'Client Observer Portal',
    text: 'For store managers, area managers and head office. Sign in with the Google account the audit team registered for you to follow your schedules live.',
    button: 'Sign in & open Client Portal',
  },
};

function Login() {
  const navigate = useNavigate();
  const {
    login,
    loading,
    error,
    isAuthenticated,
    isFirebaseConfigured,
    user,
    setError,
  } = useAuth();
  const [mode, setMode] = useState(() => {
    try {
      return localStorage.getItem('loginMode') === 'client' ? 'client' : 'internal';
    } catch {
      return 'internal';
    }
  });

  const missingKeys = getMissingFirebaseKeys();
  const current = MODES[mode];

  // Client users only see their audits; everyone else lands on the dashboard.
  // The account's role decides, whichever tab was picked.
  const homeFor = (account) => (account?.role_name === 'client' ? '/client' : '/dashboard');

  useEffect(() => {
    if (isAuthenticated) {
      navigate(homeFor(user), { replace: true });
    }
  }, [isAuthenticated, navigate, user]);

  const chooseMode = (next) => {
    setMode(next);
    try {
      localStorage.setItem('loginMode', next);
    } catch {
      // Remembering the tab is only a convenience.
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const response = await login();
      navigate(homeFor(response?.user), { replace: true });
    } catch {
      // error is handled in context
    }
  };

  return (
    <div className="login-page">
      <div className="login-shell">
        <section className="login-hero">
          <div className="login-logo" aria-label="SIA — The Complete Solutions">
            <div className="login-logo__card">
              <span className="login-logo__word">
                <span className="login-logo__dots" aria-hidden="true">
                  <i />
                  <i />
                  <i />
                  <i />
                  <i />
                </span>
                SIA
              </span>
              <span className="login-logo__tagline">The Complete Solutions !</span>
            </div>
          </div>

          <span className="login-pill">
            <SignalIcon />
            Real-Time Cloud Platform
          </span>

          <h1>Zero Docking Lag. Live On-Site Stock Audit Intelligence.</h1>
          <p className="login-hero__lead">
            Phones sync scans to the cloud as you count — no cable docking. Scans are checked
            against the Article Master Data (AMD) and variance is ready the moment counting ends.
          </p>

          <ul className="login-hero__features">
            <li>
              <CheckIcon />
              Live counts, zones and discrepancies in the schedule portal
            </li>
            <li>
              <CheckIcon />
              Instant EAN validation on the Android app
            </li>
            <li>
              <CheckIcon />
              Book stock upload, recount requests and client sign-off
            </li>
          </ul>

          <div className="login-hero__note">
            <strong>
              <ShieldIcon />
              Separate role portals
            </strong>
            <p>
              Auditors and admins use the Operations Console; store managers and head office use
              the Client Observer Portal.
            </p>
          </div>
        </section>

        <section className="login-card">
          <p className="login-card__eyebrow">Select sign-in mode</p>
          <div className="login-modes" role="tablist" aria-label="Sign-in mode">
            {Object.entries(MODES).map(([key, item]) => {
              const Icon = item.icon;
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={mode === key}
                  className={`login-modes__tab${mode === key ? ' login-modes__tab--active' : ''}`}
                  onClick={() => chooseMode(key)}
                >
                  <Icon />
                  {item.label}
                </button>
              );
            })}
          </div>

          <div className="login-card__body" role="tabpanel">
            <h2>{current.title}</h2>
            <p className="login-card__text">{current.text}</p>

            {!isFirebaseConfigured && (
              <div className="login-card__warning" role="alert">
                <strong>Firebase not configured</strong>
                <p>
                  Create <code>admin/.env</code> from <code>.env.example</code> and add your
                  Firebase web app keys, then restart the dev server.
                </p>
                <ul>
                  {missingKeys.map((key) => (
                    <li key={key}>
                      <code>{key}</code>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {error && (
              <div className="login-card__error" role="alert">
                <span>{error}</span>
                <button type="button" onClick={() => setError('')}>
                  Dismiss
                </button>
              </div>
            )}

            <button
              type="button"
              className="login-submit"
              onClick={handleGoogleLogin}
              disabled={loading || !isFirebaseConfigured}
            >
              <span className="login-submit__google">
                <GoogleIcon />
              </span>
              <span>{loading ? 'Signing in…' : current.button}</span>
              <svg className="login-submit__arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
                <path d="M9 6l6 6-6 6" />
              </svg>
            </button>

            <p className="login-card__hint">
              {mode === 'client'
                ? 'No access yet? Ask the audit team to add your email on the Clients page.'
                : 'Field auditors sign in on the Android app with their email and MPIN.'}
            </p>
          </div>

          <p className="login-card__support">
            Need help signing in? Contact{' '}
            <a href="mailto:support@siasolutions.in">support@siasolutions.in</a>
          </p>
        </section>
      </div>

      <footer className="login-footer">
        © {new Date().getFullYear()} SIA Solutions · Real-time Android stock audit and
        verification
      </footer>
    </div>
  );
}

export default Login;
